// These helpers are calling this template's own server-side routes
// so, they are not directly calling Marketplace API or Integration API.
// You can find these api endpoints from 'server/api/...' directory

import appSettings from '../config/settings';
import { types as sdkTypes, transit } from './sdkLoader';
import Decimal from 'decimal.js';

export const apiBaseUrl = marketplaceRootURL => {
  const port = process.env.REACT_APP_DEV_API_SERVER_PORT;
  const useDevApiServer = process.env.NODE_ENV === 'development' && !!port;
  const envRootURL = process.env.REACT_APP_MARKETPLACE_ROOT_URL;

  // In development, the dev API server is running in a different port
  if (useDevApiServer) {
    return `http://localhost:${port}`;
  }

  // Otherwise, use the given marketplaceRootURL parameter or the same domain and port as the frontend
  return marketplaceRootURL
    ? marketplaceRootURL.replace(/\/$/, '')
    : typeof window !== 'undefined'
    ? `${window.location.origin}`
    : envRootURL
    ? envRootURL.replace(/\/$/, '')
    : '';
};

// Application type handlers for JS SDK.
//
// NOTE: keep in sync with `typeHandlers` in `server/api-util/sdk.js`
export const typeHandlers = [
  // Use Decimal type instead of SDK's BigDecimal.
  {
    type: sdkTypes.BigDecimal,
    customType: Decimal,
    writer: v => new sdkTypes.BigDecimal(v.toString()),
    reader: v => new Decimal(v.value),
  },
];

const serialize = data => {
  return transit.write(data, { typeHandlers, verbose: appSettings.sdk.transitVerbose });
};

const deserialize = str => {
  return transit.read(str, { typeHandlers });
};

const methods = {
  POST: 'POST',
  GET: 'GET',
  PUT: 'PUT',
  PATCH: 'PATCH',
  DELETE: 'DELETE',
};

// If server/api returns data from SDK, you should set Content-Type to 'application/transit+json'
const request = (path, options = {}) => {
  const url = `${apiBaseUrl()}${path}`;
  const { credentials, headers, body, ...rest } = options;

  // If headers are not set, we assume that the body should be serialized as transit format.
  const shouldSerializeBody =
    (!headers || headers['Content-Type'] === 'application/transit+json') && body;
  const bodyMaybe = body
    ? {
        body: shouldSerializeBody ? serialize(body) : body,
      }
    : {};

  const fetchOptions = {
    credentials: credentials || 'include',
    // Since server/api mostly talks to Marketplace API using SDK,
    // we default to 'application/transit+json' as content type (as SDK uses transit).
    headers: headers || { 'Content-Type': 'application/transit+json' },
    ...bodyMaybe,
    ...rest,
  };

  const fetchImpl = typeof window !== 'undefined' ? window.fetch : global.fetch;

  return fetchImpl(url, fetchOptions).then(res => {
    const contentTypeHeader = res.headers.get('Content-Type');
    const contentType = contentTypeHeader ? contentTypeHeader.split(';')[0] : null;

    if (res.status >= 400) {
      return res.json().then(data => {
        let e = new Error();
        e = Object.assign(e, data);

        throw e;
      });
    }
    if (contentType === 'application/transit+json') {
      return res.text().then(deserialize);
    } else if (contentType === 'application/json') {
      return res.json();
    }
    return res.text();
  });
};

// Keep the previous parameter order for the post method.
// For now, only POST has own specific function, but you can create more or use request directly.
const post = (path, body, options = {}) => {
  const requestOptions = {
    ...options,
    method: methods.POST,
    body,
  };

  return request(path, requestOptions);
};

const postJson = (path, body, options = {}) => {
  return request(path, {
    ...options,
    method: methods.POST,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body || {}),
  });
};

// Fetch transaction line items from the local API endpoint.
//
// See `server/api/transaction-line-items.js` to see what data should
// be sent in the body.
export const transactionLineItems = body => {
  return post('/api/transaction-line-items', body);
};

// Initiate a privileged transaction.
//
// With privileged transitions, the transactions need to be created
// from the backend. This endpoint enables sending the order data to
// the local backend, and passing that to the Marketplace API.
//
// See `server/api/initiate-privileged.js` to see what data should be
// sent in the body.
export const initiatePrivileged = body => {
  return post('/api/initiate-privileged', body);
};

// Transition a transaction with a privileged transition.
//
// This is similar to the `initiatePrivileged` above. It will use the
// backend for the transition. The backend endpoint will add the
// payment line items to the transition params.
//
// See `server/api/transition-privileged.js` to see what data should
// be sent in the body.
export const transitionPrivileged = body => {
  return post('/api/transition-privileged', body);
};

// Confirm cart child stock reservations after parent payment succeeds.
export const confirmStock = body => {
  return post('/api/confirm-stock', body);
};

// Create user with identity provider (e.g. Facebook or Google)
//
// If loginWithIdp api call fails and user can't authenticate to Marketplace API with idp
// we will show option to create a new user with idp.
// For that user needs to confirm data fetched from the idp.
// After the confirmation, this endpoint is called to create a new user with confirmed data.
//
// See `server/api/auth/createUserWithIdp.js` to see what data should
// be sent in the body.
export const createUserWithIdp = body => {
  return post('/api/auth/create-user-with-idp', body);
};

// Check if user can be deleted and then delete the user. Endpoint logic
// must be modified to accommodate the transaction processes used in
// the marketplace.
export const deleteUserAccount = body => {
  return post('/api/delete-account', body);
};

export const fetchSimilarSellers = body => {
  return post('/api/similar-sellers', body);
};

export const fetchSearchSellers = body => {
  return post('/api/search-sellers', body);
};

/**
 * Submit a seller application: sets metadata.sellerApproved = false and emails admins.
 * Profile seller fields are saved separately via currentUser.updateProfile.
 */
export const submitSellerApplication = body => {
  return postJson('/api/seller-application', body);
};

/**
 * Store an unsupported-country seller lead (country) in Brevo for the logged-in user.
 */
export const submitExpansionLead = body => {
  return postJson('/api/expansion-lead', body);
};

/**
 * Notify admins via Brevo after a customer dispute transition succeeds.
 */
export const notifyDispute = body => {
  return postJson('/api/dispute-notification', body);
};

export const fetchShippingEligibility = body => {
  return postJson('/api/shipping/eligibility', body);
};

export const validateShippingAddress = body => {
  return postJson('/api/shipping/validate-address', body);
};

export const fetchShippingQuotes = body => {
  return postJson('/api/shipping/quote', body);
};

export const fetchShippingPickupPoints = body => {
  return postJson('/api/shipping/pickup-points', body);
};

export const verifyShippingPickupPoint = body => {
  return postJson('/api/shipping/verify-pickup-point', body);
};

export const purchaseShippingLabel = body => {
  return postJson('/api/shipping/purchase-label', body);
};

export const openShippingLabelPdf = transactionId => {
  return window.open(
    `${apiBaseUrl()}/api/shipping/label-pdf?transactionId=${transactionId}`,
    '_blank'
  );
};

/**
 * Fetch Frankfurter/ECB FX rate (and optional converted amount in subunits).
 * @param {Object} params
 * @param {string} params.from - ISO currency code
 * @param {string} params.to - ISO currency code
 * @param {number} [params.amount] - Optional amount in subunits to convert
 */
export const fetchExchangeRate = ({ from, to, amount } = {}) => {
  const search = new URLSearchParams();
  if (from) {
    search.set('from', from);
  }
  if (to) {
    search.set('to', to);
  }
  if (amount != null) {
    search.set('amount', `${amount}`);
  }
  return request(`/api/exchange-rate?${search.toString()}`, {
    method: methods.GET,
    headers: { Accept: 'application/json' },
  });
};
